/**
 * Textos (`ca`) del tall «evidence» de Qualitat i APPCC (OBR-171). Tall propi: no es toca `ca.ts`.
 * Les tres llengües han de tenir les mateixes claus (i18n.test.ts); cap text literal a les vistes.
 *
 * Les claus de les famílies (`block_*`, `metric_*`, `homologationStatus_*`…) les resol `labels.ts` del BackOffice
 * a partir dels enums de `@obrava/shared`; `labels.test.ts` comprova que cada valor de cada enum hi és a les tres
 * llengües. El PDF i l'Excel porten aquests mateixos textos (es resolen abans de cridar els generadors).
 */

export const evidenceCa = {
  'backoffice.qualityEvidence': {
    // ── Pantalla ──────────────────────────────────────────────────────────────────────────
    subtitle:
      'Genera el dossier amb els registres de qualitat d’un període, a punt per mostrar-lo en una inspecció.',
    periodTitle: 'Període',
    fromLabel: 'Des del',
    toLabel: 'Fins al',
    periodIssue_from_invalid: 'La data inicial no és vàlida.',
    periodIssue_to_invalid: 'La data final no és vàlida.',
    periodIssue_from_after_to: 'La data inicial no pot ser posterior a la final.',
    blocksTitle: 'Què s’hi inclou',
    blockHint_homologation:
      'Cada proveïdor amb el seu estat, els canvis d’estat del període i els documents (registre sanitari, certificats).',
    blockHint_receptionControls:
      'Les línies rebudes en albarans confirmats, amb temperatura, envasat, etiquetatge, decisió i qui ho va registrar.',
    blockHint_selfChecks:
      'Els autocontrols fets (qui, quan i resultat, amb els valors fora de rang) i els que tocaven i no s’han fet.',
    blockHint_nonConformities:
      'Les no conformitats obertes en el període amb les seves accions correctores i el seu historial.',
    blockHint_training:
      'Cada manipulador amb les seves formacions (tema, data, entitat i caducitat) i el seu estat.',
    noBlockSelected: 'Marca almenys un bloc per poder descarregar.',
    downloadPdf: 'Descarrega el PDF',
    downloadExcel: 'Descarrega l’Excel',
    preparing: 'Preparant el fitxer…',
    downloadError: 'No s’ha pogut generar el fitxer: {{message}}',
    brandingError:
      'No s’ha pogut llegir la identitat de l’obrador (nom, logotip, registre sanitari): {{message}}',
    previewTitle: 'Registres del període',
    previewLoading: 'Llegint els registres…',
    previewUnavailable: 'No s’ha pogut llegir: {{message}}',
    retry: 'Torna-ho a provar',
    footnote:
      'Les dades es llegeixen en el moment de descarregar. Aquesta pantalla només consulta: no modifica cap registre.',

    // ── Document (PDF i Excel) ────────────────────────────────────────────────────────────
    documentTitle: 'Evidències per a inspecció',
    period: 'Període',
    generatedOn: 'Generat el',
    generatedBy: 'Generat per',
    registry: 'Registre sanitari',
    summaryTitle: 'Resum',
    colBlock: 'Bloc',
    colIndicator: 'Indicador',
    colValue: 'Valor',
    emptyBlock: 'Cap registre en aquest període.',
    unavailableBlock:
      'Aquest bloc no s’ha pogut llegir en generar el document. No vol dir que no hi hagi registres.',
    emptyEvents: 'Cap canvi d’estat en aquest període.',
    emptyDocuments: 'Cap document registrat.',
    emptyMissed: 'Cap autocontrol sense fer.',
    emptyRecords: 'Cap autocontrol fet en aquest període.',
    emptyActions: 'Cap acció correctora.',
    emptyNcEvents: 'Cap esdeveniment.',
    pageOf: 'Pàgina {{page}} de {{total}}',
    periodRange: '{{from}} – {{to}}',
    valuePhoto: 'foto adjunta',
    flagOutOfRange: 'fora de rang',
    flagExpired: 'caducat',
    flagExpiredFeminine: 'caducada',
    noExpiry: 'No caduca',
    rangeBoth: '{{min}} – {{max}}',
    rangeMin: 'mín. {{value}}',
    rangeMax: 'màx. {{value}}',

    // Taules
    tableSuppliers: 'Proveïdors i estat d’homologació',
    tableHomologationEvents: 'Canvis d’estat en el període',
    tableDocuments: 'Documents dels proveïdors',
    tableReceptionLines: 'Línies rebudes amb el seu control',
    tableRecords: 'Autocontrols fets',
    tableMissed: 'Autocontrols sense fer',
    tableNonConformities: 'No conformitats',
    tableActions: 'Accions correctores',
    tableNcEvents: 'Historial de les no conformitats',
    tableTrainings: 'Formacions registrades',

    // Columnes
    colSupplier: 'Proveïdor',
    colCode: 'Codi',
    colStatus: 'Estat',
    colStatusAt: 'Data i hora',
    colResponsible: 'Responsable',
    colNotes: 'Observacions',
    colChangedAt: 'Data i hora',
    colFrom: 'De',
    colTo: 'A',
    colReason: 'Motiu',
    colBy: 'Fet per',
    colDocType: 'Tipus',
    colDocName: 'Document',
    colDocNumber: 'Número',
    colIssuedOn: 'Emissió',
    colExpiresOn: 'Caducitat',
    colReceptionDate: 'Data',
    colReceptionNumber: 'Albarà',
    colArticle: 'Article',
    colInternalLot: 'Lot intern',
    colSupplierLot: 'Lot del proveïdor',
    colQuantity: 'Quantitat',
    colUnit: 'Unitat',
    colTemperature: 'Temp. (ºC)',
    colRange: 'Rang (ºC)',
    colTemperatureCheck: 'Control de temperatura',
    colPackaging: 'Envasat',
    colLabeling: 'Etiquetatge',
    colOutcome: 'Resultat',
    colRecordedBy: 'Registrat per',
    colRecordedAt: 'Data i hora del control',
    colTemplate: 'Autocontrol',
    colFrequency: 'Freqüència',
    colPeriodKey: 'Període',
    colPerformedAt: 'Data i hora',
    colResult: 'Resultat',
    colValues: 'Valors',
    colNcCode: 'Codi',
    colOrigin: 'Origen',
    colOpenedAt: 'Data i hora d’obertura',
    colReportedBy: 'Obert per',
    colDescription: 'Descripció',
    colClosedAt: 'Tancament',
    colClosedBy: 'Tancada per',
    colClosingNote: 'Nota de tancament',
    colNc: 'NC',
    colAction: 'Acció',
    colDueOn: 'Termini',
    colVerification: 'Verificació',
    colVerifiedBy: 'Verificada per',
    colEvent: 'Esdeveniment',
    colNote: 'Nota',
    colWorker: 'Persona',
    colTopic: 'Tema',
    colTrainedOn: 'Data de la formació',
    colProvider: 'Entitat formadora',

    // ── Famílies de valors ────────────────────────────────────────────────────────────────
    block_homologation: 'Homologació de proveïdors',
    block_receptionControls: 'Controls a la recepció',
    block_selfChecks: 'Autocontrols',
    block_nonConformities: 'No conformitats i accions correctores',
    block_training: 'Formació dels manipuladors',

    metric_suppliers: 'Proveïdors',
    metric_suppliersApproved: 'Homologats',
    metric_suppliersNotApproved: 'No homologats (pendents, no homologats o suspesos)',
    metric_homologationEvents: 'Canvis d’estat en el període',
    metric_documents: 'Documents',
    metric_documentsExpired: 'Documents caducats',
    metric_lines: 'Línies rebudes',
    metric_linesControlled: 'Amb control registrat',
    metric_linesNotControlled: 'Sense control registrat',
    metric_linesTemperatureOut: 'Temperatura fora de rang',
    metric_linesRejected: 'Rebutjades',
    metric_records: 'Autocontrols fets',
    metric_recordsFailed: 'Amb resultat KO',
    metric_missed: 'Autocontrols sense fer',
    metric_nonConformities: 'No conformitats obertes en el període',
    metric_nonConformitiesOpen: 'Encara obertes o en curs',
    metric_nonConformitiesClosed: 'Tancades',
    metric_actions: 'Accions correctores',
    metric_actionsOpen: 'Accions sense tancar',
    metric_workers: 'Manipuladors',
    metric_workersValid: 'Amb formació vigent',
    metric_workersExpiring: 'Amb formació que caduca aviat',
    metric_workersExpired: 'Amb formació caducada',
    metric_workersMissing: 'Sense formació',

    homologationStatus_pending: 'Pendent',
    homologationStatus_approved: 'Homologat',
    homologationStatus_not_approved: 'No homologat',
    homologationStatus_suspended: 'Suspès',

    documentType_sanitary_registry: 'Registre sanitari',
    documentType_certificate: 'Certificat',

    checkResult_ok: 'OK',
    checkResult_ko: 'KO',

    qcDecision_accepted: 'Acceptat',
    qcDecision_accepted_with_incident: 'Acceptat amb incidència',
    qcDecision_rejected: 'Rebutjat',

    outcome_not_controlled: 'Sense control registrat',
    outcome_accepted: 'Acceptat',
    outcome_accepted_with_incident: 'Acceptat amb incidència',
    outcome_rejected: 'Rebutjat',
    outcome_undecided: 'Pendent de decidir',

    temperatureStatus_not_measured: 'No mesurada',
    temperatureStatus_no_range: 'Sense rang de referència',
    temperatureStatus_ok: 'Dins del rang',
    temperatureStatus_out_of_range: 'Fora de rang',

    kind_cleaning: 'Neteja i desinfecció',
    kind_temperature: 'Temperatures',
    kind_pests: 'Control de plagues',
    kind_custom: 'Altres',

    frequency_daily: 'Diària',
    frequency_weekly: 'Setmanal',
    frequency_monthly: 'Mensual',

    ncOrigin_reception: 'Recepció',
    ncOrigin_self_check: 'Autocontrol',
    ncOrigin_production: 'Producció',
    ncOrigin_manual: 'Manual',
    ncOrigin_other: 'Altres',

    ncStatus_open: 'Oberta',
    ncStatus_in_progress: 'En curs',
    ncStatus_closed: 'Tancada',

    actionStatus_open: 'Oberta',
    actionStatus_in_progress: 'En curs',
    actionStatus_closed: 'Tancada',

    ncEvent_created: 'Creada',
    ncEvent_status_changed: 'Canvi d’estat',
    ncEvent_action_added: 'Acció afegida',
    ncEvent_action_updated: 'Acció actualitzada',
    ncEvent_closed: 'Tancada',
    ncEvent_reopened: 'Reoberta',

    trainingStatus_valid: 'Vigent',
    trainingStatus_expiring: 'Caduca aviat',
    trainingStatus_expired: 'Caducada',
    trainingStatus_missing: 'Sense formació',

    trainingTopic_food_handler: 'Manipulador d’aliments',
    trainingTopic_allergens: 'Al·lèrgens',
    trainingTopic_other: 'Altres',

    // Resum del detall d'una NC automàtica (els mateixos motius que `ncDetailSummary`)
    ncSummary_reasonTemperatureRange:
      'Temperatura {{temperature}} ºC, fora del rang {{min}} a {{max}} ºC',
    ncSummary_reasonTemperatureAbove:
      'Temperatura {{temperature}} ºC, per sobre del màxim ({{max}} ºC)',
    ncSummary_reasonTemperatureBelow:
      'Temperatura {{temperature}} ºC, per sota del mínim ({{min}} ºC)',
    ncSummary_reasonTemperature: 'Temperatura {{temperature}} ºC fora de rang',
    ncSummary_reasonTemperatureUnknown: 'Temperatura fora de rang',
    ncSummary_reasonPackagingKo: 'Envasat no conforme',
    ncSummary_reasonLabelingKo: 'Etiquetatge no conforme',
    ncSummary_reasonDecisionRejected: 'Línia rebutjada al control de recepció',
    ncSummary_reasonDecisionAcceptedWithIncident: 'Línia acceptada amb incidència',
    ncSummary_reasonSelfCheckFailed: 'Autocontrol amb resultat KO',
    ncSummary_reasonUnknown: 'Motiu: {{code}}',
    ncSummary_selfCheckFailedRange: '{{label}}: {{value}} (rang {{min}} a {{max}})',
    ncSummary_selfCheckFailedBelowMin: '{{label}}: {{value}} (mínim {{min}})',
    ncSummary_selfCheckFailedAboveMax: '{{label}}: {{value}} (màxim {{max}})',
    ncSummary_selfCheckFailedValue: '{{label}}: {{value}}',
    ncSummary_selfCheckFailedKo: '{{label}}: KO',
    ncSummary_selfCheckFailedUnnamed: 'Camp de l’autocontrol que ha fallat',
  },
} as const;
